// #region ids
import { Schema } from "effect"

/** Lowercase Crockford base32 ULID: 10 chars of ms timestamp + 16 random chars. Sorts by creation time. */
const ULID = "[0-9a-hjkmnp-tv-z]{26}"

/**
 * Minga-owned ids are `<prefix>_<ulid>`, minted by whoever creates the record (web client, DO, runner, MCP
 * tool layer). A changeset can therefore reference nodes it creates, and an optimistic client renders a node
 * before the server confirms it. `decide` rejects an id that already exists.
 */
const prefixed = (prefix: string) => Schema.String.check(Schema.isPattern(new RegExp(`^${prefix}_${ULID}$`)))

/** Better Auth mints user, organization (= workspace) and team ids (32 random alphanumerics by default). Opaque. */
const authId = Schema.String.check(Schema.isPattern(/^[A-Za-z0-9_-]{1,64}$/))

export const UserId = authId.pipe(Schema.brand("UserId"))
export type UserId = typeof UserId.Type
/** = Better Auth organization id. */
export const WorkspaceId = authId.pipe(Schema.brand("WorkspaceId"))
export type WorkspaceId = typeof WorkspaceId.Type
export const TeamId = authId.pipe(Schema.brand("TeamId"))

export const ProjectId = prefixed("prj").pipe(Schema.brand("ProjectId"))
export type ProjectId = typeof ProjectId.Type
export const AreaId = prefixed("area").pipe(Schema.brand("AreaId"))
export type AreaId = typeof AreaId.Type
export const GoalId = prefixed("goal").pipe(Schema.brand("GoalId"))
export type GoalId = typeof GoalId.Type
export const KpiId = prefixed("kpi").pipe(Schema.brand("KpiId"))
export type KpiId = typeof KpiId.Type
export const InitiativeId = prefixed("ini").pipe(Schema.brand("InitiativeId"))
export type InitiativeId = typeof InitiativeId.Type
export const TaskId = prefixed("tsk").pipe(Schema.brand("TaskId"))
export type TaskId = typeof TaskId.Type
export const EdgeId = prefixed("edge").pipe(Schema.brand("EdgeId"))
export const AgentId = prefixed("agt").pipe(Schema.brand("AgentId"))
export type AgentId = typeof AgentId.Type
export const PolicyId = prefixed("pol").pipe(Schema.brand("PolicyId"))
export const ProposalId = prefixed("prop").pipe(Schema.brand("ProposalId"))
export type ProposalId = typeof ProposalId.Type
export const ChangesetId = prefixed("chg").pipe(Schema.brand("ChangesetId"))
export const CommentId = prefixed("cmt").pipe(Schema.brand("CommentId"))
export const ArtifactId = prefixed("art").pipe(Schema.brand("ArtifactId"))
export const MemoryItemId = prefixed("mem").pipe(Schema.brand("MemoryItemId"))
export const RunId = prefixed("run").pipe(Schema.brand("RunId"))
export type RunId = typeof RunId.Type
/**
 * Runner ids in production are `rnr_` + 32 hex characters (minted by apps/cloud/src/lib/tokens.ts at pairing);
 * the ULID form is accepted too (decision 0011, P1).
 */
export const RunnerId = Schema.String.check(
  Schema.isPattern(/^rnr_(?:[0-9a-hjkmnp-tv-z]{26}|[0-9a-f]{32})$/),
).pipe(Schema.brand("RunnerId"))
export type RunnerId = typeof RunnerId.Type
export const OfferId = prefixed("ofr").pipe(Schema.brand("OfferId"))
export const RepoId = prefixed("repo").pipe(Schema.brand("RepoId"))
export const IntegrationId = prefixed("int").pipe(Schema.brand("IntegrationId"))
export const CredentialId = prefixed("cred").pipe(Schema.brand("CredentialId"))
export const MonitorId = prefixed("mon").pipe(Schema.brand("MonitorId"))
export const NotificationId = prefixed("ntf").pipe(Schema.brand("NotificationId"))
export const WakeId = prefixed("wake").pipe(Schema.brand("WakeId"))
export const CommandId = prefixed("cmd").pipe(Schema.brand("CommandId"))
/** An intake item: an idea, feature request, bug or question captured from the web, the CLI, MCP or a webhook. */
export const IntakeId = prefixed("itk").pipe(Schema.brand("IntakeId"))
export type IntakeId = typeof IntakeId.Type
/** A webhook endpoint that can trigger a routine (Phase 2). */
export const HookId = prefixed("hook").pipe(Schema.brand("HookId"))

/** Gapless position in ONE log (each state-owning DO has its own log). 0 = nothing applied yet. */
export const Seq = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)).pipe(Schema.brand("Seq"))
export type Seq = typeof Seq.Type
/** Per-run lease fencing token. 0 = never leased; +1 on every lease grant of that run. */
export const Epoch = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)).pipe(Schema.brand("Epoch"))
export type Epoch = typeof Epoch.Type
/** Transport position on one run stream, counted separately per direction. Never confused with a log Seq. */
export const FrameSeq = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0)).pipe(Schema.brand("FrameSeq"))

/** Wall-clock epoch milliseconds (UTC), stamped by the owning DO. Durations are integer ms. */
export const Millis = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
export const NonNegInt = Schema.Int.check(Schema.isGreaterThanOrEqualTo(0))
export const PosInt = Schema.Int.check(Schema.isGreaterThanOrEqualTo(1))
export const Sha256 = Schema.String.check(Schema.isPattern(/^[0-9a-f]{64}$/))

/** Per-project human handle, assigned by the Project DO in `decide`: G-1, K-3, I-7, T-412, P-58, R-2291. Display only. */
export const ShortRef = Schema.String.check(Schema.isPattern(/^[GKITPR]-[1-9][0-9]*$/)).pipe(
  Schema.brand("ShortRef"),
)
/** A temporary teammate: "W-3" (worker runs), "V-2" (validator runs). Per project, never reused (settings wave §5.1). */
export const WorkerRef = Schema.String.check(Schema.isPattern(/^[WV]-[1-9][0-9]*$/)).pipe(
  Schema.brand("WorkerRef"),
)
export type WorkerRef = typeof WorkerRef.Type

/** The rules of `git check-ref-format --branch` that matter for a base branch (moved here from the cloud). */
export const isBranchName = (name: string): boolean => {
  if (name.length === 0 || name.length > 255 || name === "@") return false
  if (name.startsWith("-") || name.startsWith("/") || name.endsWith("/") || name.endsWith(".")) return false
  // biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what this rejects
  if (/[\u0000-\u0020\u007f~^:?*[\\]/.test(name)) return false
  if (name.includes("..") || name.includes("@{") || name.includes("//")) return false
  return name.split("/").every((part) => part.length > 0 && !part.startsWith(".") && !part.endsWith(".lock"))
}
/** A git branch name `git check-ref-format --branch` accepts (settings wave §5.1). */
export const BranchName = Schema.String.check(
  Schema.makeFilter((s: string) => isBranchName(s) || "not a valid git branch name"),
)
export const Slug = Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,62}$/))
/** @-handle; people and agents share one namespace per project. */
export const Handle = Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9_-]{0,31}$/))
/** Normalised intent for meaning-based task dedupe (lowercase, dash-separated). The tool layer normalises. */
export const IntentKey = Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,79}$/))
/** Fractional index (base62) so drag-to-reorder rewrites one field, never the siblings. */
export const Rank = Schema.String.check(Schema.isPattern(/^[0-9A-Za-z]{1,64}$/))
/** Authored text; rendered sanitised (no remote images, link warnings). */
export const Markdown = Schema.String
/** Planner rule: every goal, initiative and task says why it exists. Not blank. */
export const Why = Schema.String.check(Schema.isPattern(/\S/))
/** Runner-local profile name, stable per runner: "codex-personal", "codex-every", "claude-personal". */
export const ProfileId = Schema.String.check(Schema.isPattern(/^[a-z0-9][a-z0-9-]{0,39}$/)).pipe(
  Schema.brand("ProfileId"),
)
export type ProfileId = typeof ProfileId.Type

export type IdPrefix =
  | "prj"
  | "area"
  | "goal"
  | "kpi"
  | "ini"
  | "tsk"
  | "edge"
  | "agt"
  | "pol"
  | "prop"
  | "chg"
  | "cmt"
  | "art"
  | "mem"
  | "run"
  | "rnr"
  | "ofr"
  | "repo"
  | "int"
  | "cred"
  | "mon"
  | "ntf"
  | "wake"
  | "cmd"
  | "itk"
  | "hook"

/** Edge helper (impure inputs are passed in, so the function itself is pure and testable). */
export type MintId = (prefix: IdPrefix, nowMs: number, random10: Uint8Array) => string
// #endregion ids
